import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { BanknoteIcon, CalendarIcon, LockIcon, UserIcon, CreditCardIcon, CheckCircleIcon, XCircleIcon, XIcon, LoaderIcon } from 'lucide-react';
import { formatCurrencyAmount } from '../../../theme/tokens';
type ProcessingStatus = 'idle' | 'processing' | 'success' | 'error';
export function VirtualTerminal() {
  const {
    t
  } = useTranslation('payments');
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus>('idle');
  const [reference, setReference] = useState('');
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors placeholder:text-gray-400';
  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ').substring(0, 19);
  };
  const formatExpiryDate = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length > 2) return `${digits.substring(0, 2)}/${digits.substring(2, 4)}`;
    return digits;
  };
  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCardNumber(formatCardNumber(e.target.value));
  };
  const handleExpiryDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExpiryDate(formatExpiryDate(e.target.value));
  };
  const handleCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCvv(e.target.value.replace(/\D/g, '').substring(0, 4));
  };
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d.]/g, '');
    if (value.split('.').length > 2) return;
    setAmount(value);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!cardNumber || !expiryDate || !cvv || !cardholderName || !amount) return;
    setProcessingStatus('processing');
    const newReference = `REF-${Math.floor(Math.random() * 1000000).toString().padStart(6, '0')}`;
    setReference(newReference);
    setTimeout(() => {
      setProcessingStatus(Math.random() < 0.8 ? 'success' : 'error');
    }, 2000);
  };
  const resetForm = () => {
    setCardNumber('');
    setExpiryDate('');
    setCvv('');
    setCardholderName('');
    setAmount('');
    setDescription('');
    setProcessingStatus('idle');
    setReference('');
  };
  const closeModal = () => {
    if (processingStatus === 'success') resetForm();else setProcessingStatus('idle');
  };
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const isFormValid = cardNumber && expiryDate && cvv && cardholderName && amount && consentConfirmed;
  return <div className="max-w-2xl mx-auto" data-id="element-8499">
      <form onSubmit={handleSubmit} className="space-y-5" data-id="element-8500">
        {/* ─── PAYMENT DETAILS ─── */}
        <div className="bg-white rounded-xl border border-gray-200" data-id="element-8501">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-8502">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-8503">
              <BanknoteIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-8504" />
              {t('components.FixedLinksPayment.payment_details')}
            </h2>
            <p className="text-xs text-gray-500 mt-1" data-id="element-8505">
              {t('pages.VirtualTerminal.process_a_mail_order_telephone_order')}
            </p>
          </div>

          <div className="px-6 py-4 border-b border-gray-200" data-id="element-8506">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8507">
              {t('components.BulkPaymentPreview.amount')}{' '}
              <span className="text-red-400" data-id="element-8508">*</span>
            </label>
            <div className="relative" data-id="element-8509">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8510">
                <span className="text-sm font-medium text-gray-500" data-id="element-8511">
                  {t('components.BulkPaymentListSimplified.usd')}
                </span>
              </div>
              <input type="text" className={`${inputClass} pl-12`} placeholder="0.00" value={amount} onChange={handleAmountChange} data-id="element-8512" />
            </div>
          </div>

          <div className="px-6 py-4" data-id="element-8513">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8514">
              {t('components.BulkPaymentPreview.description')}{' '}
              <span className="normal-case text-gray-400" data-id="element-8515">
                {t('components.RecurringPayment.optional')}
              </span>
            </label>
            <input type="text" className={inputClass} placeholder={t('pages.VirtualTerminal.attrs.eg_product_purchase')} value={description} onChange={e => setDescription(e.target.value)} data-id="element-8516" />
          </div>
        </div>

        {/* ─── CARD INFORMATION ─── */}
        <div className="bg-white rounded-xl border border-gray-200" data-id="element-8517">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-8518">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-8519">
              <CreditCardIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-8520" />
              {t('pages.VirtualTerminal.card_information')}
            </h2>
          </div>

          <div className="px-6 py-4 border-b border-gray-200" data-id="element-8521">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8522">
              {t('pages.VirtualTerminal.card_number')}{' '}
              <span className="text-red-400" data-id="element-8523">*</span>
            </label>
            <div className="relative" data-id="element-8524">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8525">
                <CreditCardIcon size={14} className="text-gray-400" data-id="element-8526" />
              </div>
              <input type="text" className={`${inputClass} pl-9`} placeholder={t('pages.VirtualTerminal.attrs.xxxx_xxxx_xxxx_xxxx')} value={cardNumber} onChange={handleCardNumberChange} data-id="element-8527" />
            </div>
          </div>

          <div className="px-6 py-4 border-b border-gray-200 grid grid-cols-2 gap-4" data-id="element-8528">
            <div data-id="element-8529">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8530">
                {t('components.FixedLinksPayment.expiry_date')}{' '}
                <span className="text-red-400" data-id="element-8531">*</span>
              </label>
              <div className="relative" data-id="element-8532">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8533">
                  <CalendarIcon size={14} className="text-gray-400" data-id="element-8534" />
                </div>
                <input type="text" className={`${inputClass} pl-9`} placeholder={t('pages.VirtualTerminal.attrs.mmyy')} value={expiryDate} onChange={handleExpiryDateChange} data-id="element-8535" />
              </div>
            </div>
            <div data-id="element-8536">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8537">
                {t('pages.VirtualTerminal.cvv_cvc')}{' '}
                <span className="text-red-400" data-id="element-8538">*</span>
              </label>
              <div className="relative" data-id="element-8539">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8540">
                  <LockIcon size={14} className="text-gray-400" data-id="element-8541" />
                </div>
                <input type="text" className={`${inputClass} pl-9`} placeholder={t('pages.VirtualTerminal.attrs.xxx')} value={cvv} onChange={handleCvvChange} data-id="element-8542" />
              </div>
            </div>
          </div>

          <div className="px-6 py-4" data-id="element-8543">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8544">
              {t('pages.VirtualTerminal.cardholder_name')}{' '}
              <span className="text-red-400" data-id="element-8545">*</span>
            </label>
            <div className="relative" data-id="element-8546">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8547">
                <UserIcon size={14} className="text-gray-400" data-id="element-8548" />
              </div>
              <input type="text" className={`${inputClass} pl-9`} placeholder={t('pages.VirtualTerminal.attrs.name_as_it_appears_on_card')} value={cardholderName} onChange={e => setCardholderName(e.target.value)} data-id="element-8549" />
            </div>
          </div>
        </div>

        {/* ─── SECURITY NOTE ─── */}
        <div className="flex items-center gap-2 px-1 text-xs text-gray-400" data-id="element-8550">
          <LockIcon size={12} className="flex-shrink-0" data-id="element-8551" />
          <span data-id="element-8552">
            {t('pages.VirtualTerminal.card_information_is_securely_processed_and')}
          </span>
        </div>

        {/* ─── CARDHOLDER CONSENT ─── */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-id="element-8553">
          <label className="flex items-start gap-3 cursor-pointer" data-id="element-8554">
            <input type="checkbox" checked={consentConfirmed} onChange={e => setConsentConfirmed(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#E2231A] focus:ring-[#E2231A] focus:ring-offset-0 cursor-pointer flex-shrink-0" data-id="element-8555" />
            <span className="text-sm text-amber-900 leading-relaxed" data-id="element-8556">
              I confirm that I have obtained the cardholder's consent to process
              this card payment for the amount specified above.
            </span>
          </label>
        </div>

        {/* ─── SUBMIT ─── */}
        <div className="flex justify-end" data-id="element-8557">
          <button type="submit" disabled={!isFormValid} className={`px-6 py-2.5 text-sm font-medium rounded-lg transition-colors shadow-sm ${isFormValid ? 'bg-[#E2231A] hover:bg-[#C41B14] text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-8558">
            {t('pages.VirtualTerminal.process_payment')}
          </button>
        </div>
      </form>

      {/* ─── RESULT MODAL ─── */}
      {processingStatus !== 'idle' && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{
      margin: 0,
      background: 'rgba(17,24,39,0.5)'
    }} data-id="element-8559">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden" data-id="element-8560">
              {/* Processing */}
              {processingStatus === 'processing' && <div className="p-8 flex flex-col items-center" data-id="element-8561">
                  <div className="w-14 h-14 rounded-full bg-[#E2231A]/10 flex items-center justify-center mb-4" data-id="element-8562">
                    <LoaderIcon size={24} className="text-[#9F140F] animate-spin" data-id="element-8563" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-1" data-id="element-8564">
                    {t('pages.VirtualTerminal.processing_payment')}
                  </h3>
                  <p className="text-sm text-gray-500 text-center" data-id="element-8565">
                    {t('pages.VirtualTerminal.please_wait_while_we_process_the')}
                  </p>
                </div>}

              {/* Success */}
              {processingStatus === 'success' && <>
                  <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-8566">
                    <h3 className="text-base font-semibold text-gray-900" data-id="element-8567">
                      {t('pages.VirtualTerminal.payment_successful')}
                    </h3>
                    <button onClick={closeModal} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-8568">
                      <XIcon size={18} aria-hidden="true" data-id="element-8569" />
                    </button>
                  </div>
                  <div className="p-6 flex flex-col items-center" data-id="element-8570">
                    <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mb-4" data-id="element-8571">
                      <CheckCircleIcon size={28} className="text-green-600" data-id="element-8572" />
                    </div>
                    <p className="text-sm text-gray-500 text-center mb-5" data-id="element-8573">
                      {t('pages.VirtualTerminal.the_transaction_has_been_approved')}
                    </p>

                    {/* Transaction details */}
                    <div className="w-full bg-white rounded-lg border border-gray-200 overflow-hidden mb-5" data-id="element-8574">
                      <div className="flex justify-between items-center px-4 py-2.5 border-b border-gray-200" data-id="element-8575">
                        <span className="text-xs text-gray-500" data-id="element-8576">
                          {t('components.BulkPaymentPreview.amount')}
                        </span>
                        <span className="text-xs font-medium text-gray-900" data-id="element-8577">
                          {formatCurrencyAmount(parseFloat(amount || '0'), {
                    fractionDigits: 2
                  })}
                        </span>
                      </div>
                      <div className="flex justify-between items-center px-4 py-2.5 border-b border-gray-200" data-id="element-8578">
                        <span className="text-xs text-gray-500" data-id="element-8579">
                          {t('components.BulkPaymentPreview.reference')}
                        </span>
                        <span className="text-xs font-medium text-gray-900 font-mono" data-id="element-8580">
                          {reference}
                        </span>
                      </div>
                      <div className="flex justify-between items-center px-4 py-2.5 border-b border-gray-200" data-id="element-8581">
                        <span className="text-xs text-gray-500" data-id="element-8582">
                          {t('components.CardOnFilePayment.card')}
                        </span>
                        <span className="text-xs font-medium text-gray-900" data-id="element-8583">
                          •••• {cardNumber.slice(-4)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center px-4 py-2.5" data-id="element-8584">
                        <span className="text-xs text-gray-500" data-id="element-8585">
                          {t('pages.VirtualTerminal.date')}
                        </span>
                        <span className="text-xs font-medium text-gray-900" data-id="element-8586">
                          {new Date().toLocaleString()}
                        </span>
                      </div>
                    </div>

                    <button onClick={closeModal} className="px-5 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-8587">
                      {t('pages.VirtualTerminal.new_transaction')}
                    </button>
                  </div>
                </>}

              {/* Error */}
              {processingStatus === 'error' && <>
                  <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-8588">
                    <h3 className="text-base font-semibold text-gray-900" data-id="element-8589">
                      {t('pages.VirtualTerminal.transaction_failed')}
                    </h3>
                    <button onClick={closeModal} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-8590">
                      <XIcon size={18} aria-hidden="true" data-id="element-8591" />
                    </button>
                  </div>
                  <div className="p-6 flex flex-col items-center" data-id="element-8592">
                    <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4" data-id="element-8593">
                      <XCircleIcon size={28} className="text-red-600" data-id="element-8594" />
                    </div>
                    <p className="text-sm text-gray-500 text-center mb-4" data-id="element-8595">
                      {t('pages.VirtualTerminal.the_payment_could_not_be_processed')}
                    </p>

                    <div className="w-full bg-red-50 border border-red-100 rounded-lg p-3 mb-5" data-id="element-8596">
                      <p className="text-xs text-red-700" data-id="element-8597">
                        {t('pages.VirtualTerminal.transaction_declined_by_issuing_bank_please')}
                      </p>
                    </div>

                    <button onClick={closeModal} className="px-5 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-8598">
                      {t('pages.VirtualTerminal.try_again')}
                    </button>
                  </div>
                </>}
            </div>
          </div>, document.body)}
    </div>;
}